import { extend, setInteractionMode } from 'vee-validate'
import { required as requiredRule, email as emailRule, alpha as alphaRule } from 'vee-validate/dist/rules'
import {
  createAsyncEmailValidator,
  createAsyncPhoneValidator,
  createAsyncUsernameValidator,
} from '@/utils/asyncValidators'

// Validate on blur/submit instead of on every keystroke
setInteractionMode('lazy')

// Add the required rule
extend('required', {
  ...requiredRule,
  message: 'Ce champ est obligatoire',
})

// Add email validation rule
extend('email', {
  ...emailRule,
  message: 'Adresse email invalide',
})

// Add alpha rule
extend('alpha', {
  ...alphaRule,
  message: 'Ce champ ne peut contenir que des lettres',
})

// Add numeric rule
extend('numeric', {
  // Fix: Use Number.isNaN instead of global isNaN
  validate: value => {
    const num = Number(value)
    return !Number.isNaN(num)
  },
  message: 'Ce champ ne peut contenir que des chiffres',
})

// Add min value rule
extend('min_value', {
  validate: (value, { min }) => Number(value) >= min,
  params: ['min'],
  message: 'La valeur doit être égale ou supérieure à {min}',
})

// Add max value rule
extend('max_value', {
  validate: (value, { max }) => Number(value) <= max,
  params: ['max'],
  message: 'La valeur doit être égale ou inférieure à {max}',
})

// Add Tunisian phone number validation rule
extend('tnPhone', {
  validate: value => {
    if (value === null || value === undefined || value === '') {
      return true
    }
    const cleaned = String(value || '').replace(/\D/g, '')
    if (cleaned.length !== 8) {
      return 'Ce numéro de téléphone n\'est pas un numéro réel. Veuillez saisir un numéro de téléphone valide.'
    }
    if (!/^[24579]\d{7}$/.test(cleaned)) {
      return 'Ce numéro de téléphone n\'est pas un numéro réel. Veuillez saisir un numéro de téléphone valide.'
    }
    if (/^(\d)\1{7}$/.test(cleaned)) {
      return 'Ce numéro de téléphone n\'est pas un numéro réel. Veuillez saisir un numéro de téléphone valide.'
    }
    return true
  },
  message: 'Ce numéro de téléphone n\'est pas un numéro réel. Veuillez saisir un numéro de téléphone valide.',
})

// Add Tunisian CIN validation rule
// Tunisian CIN (Carte d'Identité Nationale) must be exactly 8 digits
extend('tnCin', {
  validate: value => {
    if (value === null || value === undefined || value === '') {
      return true // Optional field, allow empty
    }
    const cleaned = String(value || '').replace(/\D/g, '')
    if (cleaned.length !== 8) {
      return 'Le CIN doit contenir exactement 8 chiffres.'
    }
    // CIN must be all numeric digits (no letters or special characters)
    if (!/^\d{8}$/.test(cleaned)) {
      return 'Le CIN doit contenir uniquement des chiffres.'
    }
    return true
  },
  message: 'Le CIN doit contenir exactement 8 chiffres.',
})
// Add decimal price validation rule (supports string numbers and dot/comma separators)
extend('priceDecimal', {
  validate: value => {
    if (value === null || value === undefined || value === '') {
      return true
    }
    const normalized = String(value).trim().replace(',', '.')
    if (normalized === '') {
      return true
    }
    const num = Number(normalized)
    return !Number.isNaN(num) && num >= 0
  },
  message: 'Veuillez saisir un prix valide (ex: 7.500)',
})

extend('asyncEmail', {
  validate: async (value, { excludeUserId }) => {
    const validator = createAsyncEmailValidator(excludeUserId || null)
    return validator(value)
  },
  params: ['excludeUserId'],
  message: 'Cet email est déjà utilisé.',
})

extend('asyncPhone', {
  validate: async (value, { companyId, excludeUserId }) => {
    const validator = createAsyncPhoneValidator(companyId || null, excludeUserId || null)
    return validator(value)
  },
  params: ['companyId', 'excludeUserId'],
  message: 'Ce numéro de téléphone est déjà utilisé.',
})

extend('asyncUsername', {
  validate: async (value, { excludeUserId }) => {
    const validator = createAsyncUsernameValidator(excludeUserId || null)
    return validator(value)
  },
  params: ['excludeUserId'],
  message: 'Ce nom d\'utilisateur est déjà utilisé.',
})
